import React, { ReactElement, useMemo, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { ClientCompanyProduct, useListSprints, useOrganizationIncidents } from '@data';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { clientPath, incidentPath } from '@app/constants/url';
import { cardClass } from '@app/constants/styles';
import { bySprintRecency } from '@app/utils';
import { Loader } from '@ui';
import IncidentFilterBar from '../../clientReporting/product/IncidentFilterBar';
import { IncidentRow, ResolvedIncidentTable } from '../../clientReporting/product/IncidentTables';
import { sprintsWithIncidents } from '../../clientReporting/product/sources/incidentSprints';
import {
  IncidentFilters,
  IncidentView,
  buildClosedIncidents,
  buildOpenIncidents,
  hasIncidentFilters,
  matchesIncidentFilters,
  noIncidentFilters,
  productIncidentFromApi,
} from '../../clientReporting/product/sources/incidents';

interface Props {
  organizationId: string;
  clientId: string;
  clientName: string;
  products: ClientCompanyProduct[];
}

/** Every incident on the client's products in one list, newest first, with the Incidents page's filters. */
const ClientIncidents = ({ organizationId, clientId, clientName, products }: Props): ReactElement => {
  const { t } = useTranslation();
  const transitionNavigate = useViewTransitionNav();
  const [filters, setFilters] = useState<IncidentFilters>(noIncidentFilters);

  const { data: incidentData, isLoading } = useOrganizationIncidents(organizationId);
  const { data: sprintData } = useListSprints();

  const rows = useMemo(() => {
    const ownIds = new Set(products.map(product => product.id));
    const entities = (incidentData?.items ?? []).filter(entity => ownIds.has(entity.productId));
    // The client record carries no support level; each incident carries its product's, which the rows are judged by.
    const own = products.map(({ id, name }) => ({
      id,
      name,
      supportLevel: entities.find(entity => entity.productId === id)?.supportLevel,
    }));
    const incidents = entities.map(productIncidentFromApi);

    return [...buildOpenIncidents(own, incidents), ...buildClosedIncidents(own, incidents)].sort(
      (a, b) => Date.parse(b.incident.reportedAt) - Date.parse(a.incident.reportedAt),
    );
  }, [incidentData?.items, products]);

  const sprints = useMemo(
    () =>
      sprintsWithIncidents(
        [...(sprintData?.items ?? [])].sort(bySprintRecency),
        rows.map(row => row.incident.reportedAt),
      ),
    [sprintData?.items, rows],
  );

  const shown = rows.filter(row => matchesIncidentFilters(row, filters));

  const open = (row: IncidentRow<IncidentView>): void => {
    if (!row.productId) return;
    transitionNavigate(incidentPath(organizationId, row.productId, row.incident.id), 'forward', {
      state: {
        backPath: clientPath(organizationId, clientId),
        backLabel: t('actions.backToName', { name: clientName }),
      },
    });
  };

  return (
    <section className="mb-6">
      <h2 className="mb-3 text-lg font-bold text-foreground">{t('incidents.title')}</h2>
      <div className="mb-4">
        <IncidentFilterBar products={products} sprints={sprints} value={filters} onChange={setFilters} />
      </div>

      <div className={cx(cardClass, 'overflow-x-auto')}>
        {/* eslint-disable-next-line no-nested-ternary -- loading, a list, or an empty line */}
        {isLoading ? (
          <Loader />
        ) : shown.length ? (
          <div className="min-w-[48rem]">
            <ResolvedIncidentTable rows={shown} showProduct={products.length > 1} onOpen={open} />
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t(hasIncidentFilters(filters) ? 'incidents.emptyFilteredOpen' : 'incidents.noneForClient')}
          </p>
        )}
      </div>
    </section>
  );
};

export default ClientIncidents;
